import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminSidebarNavigation from '../../components/ui/AdminSidebarNavigation';
import AdminHeaderNavigation from '../../components/ui/AdminHeaderNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../components/context/ToastContext';
import repositoryContainer from '../../services/RepositoryContainer';
import WorkflowEditorModal from './components/WorkflowEditorModal';

const getApiErrorMessage = (error, fallback) => {
  if (error?.data?.message) return error.data.message;
  if (typeof error?.data === 'string' && error.data.trim()) return error.data;

  const validationErrors = error?.data?.errors;
  if (validationErrors && typeof validationErrors === 'object') {
    const messages = Object.values(validationErrors).flat().filter(Boolean);
    if (messages.length) return messages.join(' ');
  }

  return fallback;
};

const formatDate = (value) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const WorkflowCard = ({ workflow, onEdit, onDelete }) => {
  const stages = [...(workflow.stages || [])].sort((first, second) => first.order - second.order);

  return (
    <article className="rounded-xl border border-border bg-card p-4 shadow-sm transition-shadow hover:shadow-md md:p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Icon name="Workflow" size={21} />
            </div>
            <div className="min-w-0">
              <h2 className="truncate font-heading text-lg font-semibold text-foreground">{workflow.name}</h2>
              <p className="mt-1 text-xs text-muted-foreground font-caption">
                Created {formatDate(workflow.createdAt)} · {stages.length} {stages.length === 1 ? 'stage' : 'stages'}
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <Button variant="outline" size="sm" iconName="Pencil" onClick={() => onEdit(workflow)}>
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-error hover:bg-error/10 hover:text-error"
            onClick={() => onDelete(workflow)}
          >
            Delete
          </Button>
        </div>
      </div>

      <div className="mt-5 rounded-xl border border-border bg-muted/20 p-4">
        <div className="space-y-0">
          {stages.map((stage, index) => (
            <div key={stage.id || `${workflow.id}-${stage.order}`} className="relative flex gap-3 pb-5 last:pb-0">
              {index < stages.length - 1 && (
                <span className="absolute left-[15px] top-8 h-[calc(100%-1.25rem)] w-px bg-border" aria-hidden="true" />
              )}
              <span className="relative z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-primary/20 bg-card text-xs text-primary font-caption font-semibold">
                {index + 1}
              </span>
              <div className="min-w-0 pt-0.5">
                <p className="font-caption font-semibold text-foreground text-base mb-1">{stage.name}</p>
                <p className="flex items-center gap-1.5 text-sm text-muted-foreground font-caption">
                  <Icon name="User" size={14} />
                  <span className="truncate">{stage.assignedUserName || 'Unassigned'}</span>
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
};

const WorkflowManagement = () => {
  const { addToast } = useToast();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [workflows, setWorkflows] = useState([]);
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadConfiguration = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const [workflowResponse, staffResponse] = await Promise.all([
        repositoryContainer.workflow.list(),
        repositoryContainer.staff.list(),
      ]);
      setWorkflows(Array.isArray(workflowResponse) ? workflowResponse : []);
      setMembers(Array.isArray(staffResponse) ? staffResponse : []);
    } catch (error) {
      setLoadError(getApiErrorMessage(error, 'Workflow configuration could not be loaded.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration]);

  const activeMemberCount = useMemo(
    () => members.filter((member) => member?.isEnabled).length,
    [members]
  );

  const openCreate = () => {
    setEditingWorkflow(null);
    setIsEditorOpen(true);
  };

  const openEdit = (workflow) => {
    setEditingWorkflow(workflow);
    setIsEditorOpen(true);
  };

  const closeEditor = () => {
    setIsEditorOpen(false);
    setEditingWorkflow(null);
  };

  const handleSave = async (payload) => {
    setIsSaving(true);
    try {
      if (editingWorkflow) {
        await repositoryContainer.workflow.update(editingWorkflow.id, payload);
        addToast('Workflow updated successfully.', 'success');
      } else {
        await repositoryContainer.workflow.create(payload);
        addToast('Workflow created successfully.', 'success');
      }
      closeEditor();
      await loadConfiguration();
    } catch (error) {
      addToast(
        getApiErrorMessage(
          error,
          editingWorkflow ? 'The workflow could not be updated.' : 'The workflow could not be created.'
        ),
        'error'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!workflowToDelete) return;
    setIsDeleting(true);
    try {
      await repositoryContainer.workflow.delete(workflowToDelete.id);
      addToast('Workflow deleted successfully.', 'success');
      setWorkflowToDelete(null);
      await loadConfiguration();
    } catch (error) {
      addToast(getApiErrorMessage(error, 'The workflow could not be deleted.'), 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminSidebarNavigation
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed((current) => !current)}
      />

      <div className={`transition-all duration-250 ease-elegant ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <AdminHeaderNavigation isSidebarCollapsed={isSidebarCollapsed} />

        <main className="pt-24 px-4 md:px-6 lg:px-8 pb-8">
          <div className="mx-auto max-w-7xl space-y-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="mb-2 font-heading text-2xl font-bold text-foreground md:text-3xl lg:text-4xl">
                  Workflow Configuration
                </h1>
                <p className="text-sm text-muted-foreground font-caption md:text-base">
                  Define the ordered review stages used to process booking requests.
                </p>
              </div>
              <Button iconName="Plus" onClick={openCreate} disabled={isLoading || activeMemberCount === 0}>
                Create Workflow
              </Button>
            </div>

            <div className="flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
              <Icon name="Info" size={19} color="var(--color-primary)" className="mt-0.5 flex-shrink-0" />
              <p className="text-sm text-muted-foreground font-caption">
                A workflow that has already processed a request is preserved for audit history and cannot be edited or deleted.
                If that happens, create a new workflow instead.
              </p>
            </div>

            {isLoading ? (
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2" aria-label="Loading workflows">
                {[0, 1, 2, 3].map((item) => (
                  <div key={item} className="h-64 animate-pulse rounded-xl border border-border bg-card" />
                ))}
              </div>
            ) : loadError ? (
              <div className="rounded-xl border border-error/20 bg-error/5 p-6 text-center">
                <Icon name="AlertCircle" size={30} color="var(--color-error)" className="mx-auto mb-2" />
                <p className="text-sm text-error font-caption">{loadError}</p>
                <Button variant="outline" size="sm" className="mt-4" iconName="RefreshCw" onClick={loadConfiguration}>
                  Try Again
                </Button>
              </div>
            ) : workflows.length === 0 ? (
              <div className="rounded-xl border border-dashed border-border bg-card px-5 py-14 text-center">
                <Icon name="Workflow" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4 opacity-60" />
                <h2 className="font-heading text-xl font-semibold text-foreground">No workflows configured</h2>
                <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground font-caption">
                  Create a workflow to define who reviews each stage of a booking request.
                </p>
                {activeMemberCount > 0 ? (
                  <Button className="mt-5" iconName="Plus" onClick={openCreate}>Create Workflow</Button>
                ) : (
                  <p className="mt-5 text-sm text-warning font-caption">
                    Add an active team member before creating a workflow.
                  </p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
                {workflows.map((workflow) => (
                  <WorkflowCard
                    key={workflow.id}
                    workflow={workflow}
                    onEdit={openEdit}
                    onDelete={setWorkflowToDelete}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      <WorkflowEditorModal
        isOpen={isEditorOpen}
        onClose={closeEditor}
        onSubmit={handleSave}
        workflow={editingWorkflow}
        members={members}
        isSubmitting={isSaving}
      />

      <Modal
        isOpen={Boolean(workflowToDelete)}
        onClose={() => !isDeleting && setWorkflowToDelete(null)}
        title="Delete Workflow"
      >
        <p className="text-sm text-muted-foreground font-caption">
          Delete <span className="font-medium text-foreground">{workflowToDelete?.name}</span>? This cannot be undone.
          Workflows that have already been used will be protected by the server and cannot be deleted.
        </p>
        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => setWorkflowToDelete(null)} disabled={isDeleting}>
            Keep Workflow
          </Button>
          <Button variant="danger" loading={isDeleting} onClick={handleDelete}>
            Delete Workflow
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default WorkflowManagement;
